/**
 * Typed error vocabulary for the data layer.
 *
 * Services + stores + composables all throw / catch these specific
 * classes so the UI can decide what to render (a toast, a sign-in
 * redirect, a retry button) without parsing error.message.
 *
 *   try { await store.fetchList() }
 *   catch (err) {
 *     if (err instanceof AuthError)    router.push('/auth/sign-in')
 *     if (err instanceof NetworkError) toast.warn('Offline')
 *     if (err.isTransient)             retry()
 *     else                              toast.error(err.message)
 *   }
 */

/** Whatever the server sent alongside a failure — an error envelope, usually. */
export type ErrorBody = Record<string, any> | null

export interface ApiErrorOptions {
  status?: number | null
  code?: string | null
  requestId?: string | null
  data?: ErrorBody
  cause?: unknown
}

/** Base class. Useful for `err instanceof ApiError` catch-alls. */
export class ApiError extends Error {
  status: number | null
  code: string | null
  /** The API stamps one on every error; quote it in a bug report. */
  requestId: string | null
  data: ErrorBody
  override cause: unknown

  constructor(message: string, opts: ApiErrorOptions = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = opts.status ?? null
    this.code = opts.code ?? null
    this.requestId = opts.requestId ?? null
    this.data = opts.data ?? null
    this.cause = opts.cause ?? null
  }
}

/** Non-2xx HTTP responses. `status` is the actual code. */
export class HttpError extends ApiError {
  /** Narrowed from the base class: an HTTP error always has one. */
  override status: number

  constructor(status: number, message?: string, data?: ErrorBody) {
    super(message || `HTTP ${status}`, { status, data })
    this.name = 'HttpError'
    this.status = status
  }
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500
  }
  get isServerError(): boolean {
    return this.status >= 500
  }
}

/** Auth failure (401, 403). UI should redirect to sign-in. */
export class AuthError extends HttpError {
  constructor(status: number, message?: string, data?: ErrorBody) {
    super(status, message || 'Authentication required', data)
    this.name = 'AuthError'
  }
}

/** Validation failure (422). `data.fields` typically has per-field errors. */
export class ValidationError extends HttpError {
  fields: Record<string, string | string[]>

  constructor(message?: string, data?: ErrorBody) {
    super(422, message || 'Validation failed', data)
    this.name = 'ValidationError'
    this.fields = data?.fields ?? {}
  }
}

/** Transport-level failure: offline, DNS, CORS, aborted. */
export class NetworkError extends ApiError {
  constructor(message = 'Network unreachable', cause?: unknown) {
    super(message, { cause })
    this.name = 'NetworkError'
  }
}

/** Request exceeded the configured timeout. */
export class TimeoutError extends ApiError {
  constructor(message = 'Request timed out') {
    super(message)
    this.name = 'TimeoutError'
  }
}

/**
 * Whether an error is worth retrying. Transients: network, timeout,
 * 502/503/504. Auth + validation are not — retrying won't help.
 */
export function isTransient(err: unknown): boolean {
  if (err instanceof NetworkError) return true
  if (err instanceof TimeoutError) return true
  if (err instanceof HttpError && [502, 503, 504].includes(err.status)) return true
  return false
}

/**
 * Re-invoke a thunk with exponential backoff until it succeeds or
 * exceeds `attempts`. Only retries transient errors.
 *
 *   const data = await withRetry(() => http.get('/big'), { attempts: 4 })
 */
export interface RetryOptions {
  attempts?: number
  baseDelayMs?: number
  factor?: number
}

export async function withRetry<T>(
  thunk: () => Promise<T>,
  { attempts = 3, baseDelayMs = 200, factor = 2 }: RetryOptions = {}
): Promise<T> {
  let attempt = 0
  while (true) {
    try {
      return await thunk()
    } catch (err) {
      attempt += 1
      if (attempt >= attempts || !isTransient(err)) throw err
      const wait = baseDelayMs * Math.pow(factor, attempt - 1)
      await new Promise((r) => setTimeout(r, wait))
    }
  }
}

/**
 * Normalize whatever an `await fetch(...)` chain throws into a
 * proper ApiError subclass. The HTTP client uses this to keep
 * downstream code from having to inspect raw Response objects.
 */
/**
 * Pull the human-readable parts out of the API's error envelope.
 *
 * The backend answers every failure with
 *   { "error": { "code", "message", "details": [], "request_id" } }
 * so `body.error` is an *object*. Reading it as the message — which is
 * what this did — renders "[object Object]" in the UI and throws away
 * both the code the UI branches on and the request id support needs.
 */
function unwrap(body: ErrorBody): { message?: string; code?: string; requestId?: string } {
  const envelope = body?.error
  if (envelope && typeof envelope === 'object') {
    return {
      message: typeof envelope.message === 'string' ? envelope.message : undefined,
      code: typeof envelope.code === 'string' ? envelope.code : undefined,
      requestId: typeof envelope.request_id === 'string' ? envelope.request_id : undefined,
    }
  }
  return {
    message: typeof body?.message === 'string' ? body.message : undefined,
    code: typeof body?.code === 'string' ? body.code : undefined,
  }
}

export function fromResponse(response: Response, body?: ErrorBody): HttpError {
  const status = response.status
  const { message, code, requestId } = unwrap(body ?? null)
  const text = message || response.statusText
  const err =
    status === 401 || status === 403
      ? new AuthError(status, text, body)
      : status === 422
        ? new ValidationError(text, body)
        : new HttpError(status, text, body)
  if (code) err.code = code
  if (requestId) err.requestId = requestId
  return err
}
